// 静态资源统一管理

/**
 * 图片资源路径
 */
export const images = {
  // 登录背景
  loginBackground: "/images/LoginBackground.jpg",
  loginBackground1: "/images/LoginBackground1.jpg",

  // 头像
  anonymousAvatar: "/images/anonymous_avatar.jpg",
  xiongAvatar: "/images/xiong.jpg",

  // 横幅
  banner1: "/images/banner1.png",

  // 状态图标
  emptyStatus: "/images/empty-status.png",

  // 默认头像
  defaultAvatar: "/images/anonymous_avatar.jpg",
} as const;

/**
 * 字体资源路径
 */
export const fonts = {
  roboto: "/fonts/",
  material: "/fonts/",
} as const;

/**
 * 图标资源路径
 */
export const icons = {
  jsonEditor: "/jsoneditor-icons.svg",
} as const;

/**
 * 获取图片URL的工具函数
 */
export function getImageUrl(imageName: keyof typeof images): string {
  return images[imageName];
}

/**
 * 获取头像URL，支持回退
 */
export function getAvatarUrl(avatarPath?: string): string {
  if (avatarPath) {
    // 如果是相对路径，添加前缀
    if (avatarPath.startsWith("/")) {
      return avatarPath;
    }
    // 如果是完整URL，直接返回
    if (avatarPath.startsWith("http")) {
      return avatarPath;
    }
    // 其他情况添加images前缀
    return `/images/${avatarPath}`;
  }

  return images.defaultAvatar;
}

/**
 * 预加载图片
 */
export function preloadImage(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * 批量预加载图片
 */
export async function preloadImages(srcs: string[]): Promise<void> {
  const promises = srcs.map((src) => preloadImage(src));
  await Promise.all(promises);
}

/**
 * 预加载关键图片资源
 */
export async function preloadCriticalImages(): Promise<void> {
  const criticalImages = [images.loginBackground, images.defaultAvatar, images.emptyStatus];

  await preloadImages(criticalImages);
}

/**
 * 检查图片是否存在
 */
export function checkImageExists(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

/**
 * 获取图片尺寸
 */
export function getImageSize(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * 生成缩略图URL（如果后端支持）
 */
export function getThumbnailUrl(originalUrl: string, width: number, height: number): string {
  // 这里可以根据实际的缩略图服务配置
  // 例如：return `${originalUrl}?w=${width}&h=${height}&fit=crop`
  return originalUrl;
}

/**
 * 资源预加载管理器
 */
export class AssetManager {
  private static instance: AssetManager;
  private loadedImages = new Set<string>();
  private loadingImages = new Map<string, Promise<void>>();

  static getInstance(): AssetManager {
    if (!AssetManager.instance) {
      AssetManager.instance = new AssetManager();
    }
    return AssetManager.instance;
  }

  /**
   * 预加载图片
   */
  async preloadImage(src: string): Promise<void> {
    if (this.loadedImages.has(src)) {
      return;
    }

    if (this.loadingImages.has(src)) {
      return this.loadingImages.get(src)!;
    }

    const promise = preloadImage(src)
      .then(() => {
        this.loadedImages.add(src);
        this.loadingImages.delete(src);
      })
      .catch((error) => {
        this.loadingImages.delete(src);
        throw error;
      });

    this.loadingImages.set(src, promise);
    return promise;
  }

  /**
   * 批量预加载
   */
  async preloadImages(srcs: string[]): Promise<void> {
    const promises = srcs.map((src) => this.preloadImage(src));
    await Promise.all(promises);
  }

  /**
   * 检查是否已加载
   */
  isLoaded(src: string): boolean {
    return this.loadedImages.has(src);
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    this.loadedImages.clear();
    this.loadingImages.clear();
  }
}

// 导出单例实例
export const assetManager = AssetManager.getInstance();
